import { Inspection } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const MAX_PHOTOS_PER_ITEM = 4;

export type InspectionPhotoStatus = 'uploading' | 'done' | 'error';

export interface InspectionPhoto {
  /** Stable for the life of the thumbnail, across upload, failure and retry. */
  key: string;
  subCategoryItemId: number;
  status: InspectionPhotoStatus;
  /** Bytes actually on the wire, not a guess. */
  percent: number;
  name: string;
  /** Object url while it uploads, signed url once it is stored. */
  src?: string;
  documentId?: string;
}

export interface IInspectionPhotosHook {
  photosByItem: Map<number, InspectionPhoto[]>;
  uploadingCount: number;
  failedCount: number;
  add: (subCategoryItemId: number, files: File[]) => void;
  remove: (key: string) => void;
  retry: (key: string) => void;
  retryAllFailed: () => void;
}

let photoKeySeed = 0;
const nextKey = () => `photo-${(photoKeySeed += 1)}`;

/**
 * Every photo uploads on its own, the moment it is picked.
 *
 * They used to ride along with the save as base64 - a whole checklist of camera files in one body,
 * which is what tripped the 30s timeout, and one bad file took the rest down with it. One request per
 * photo means a failure costs that photo and nothing else, and there is a real percentage to show
 * instead of a spinner that tells nobody anything.
 */
export const useInspectionPhotos = (
  inspectionId: string | undefined,
  inspection: Inspection | undefined
): IInspectionPhotosHook => {
  const { inspectionStore } = useStore();

  const [photos, setPhotos] = useState<InspectionPhoto[]>([]);

  // A mirror of the list that callbacks can read without being rebuilt every time a percentage
  // ticks - upload progress would otherwise churn every handler on the screen.
  const photosRef = useRef<InspectionPhoto[]>([]);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // Files and abort handles are kept out of state: they are not rendered, and putting a File in
  // state only invites a re-render per photo.
  const filesRef = useRef<Map<string, File>>(new Map());
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const seededForRef = useRef<string | undefined>(undefined);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
      controllersRef.current.forEach((controller) => controller.abort());
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    []
  );

  // A different inspection, or none at all: clear before the new one loads, so the last inspection's
  // photos are not briefly drawn against whichever of its item ids the new one happens to share.
  useEffect(() => {
    seededForRef.current = undefined;
    setPhotos([]);
  }, [inspectionId]);

  // Seed once per inspection. Re-seeding on every change of the inspection object is how in-flight
  // uploads used to vanish from under the user.
  useEffect(() => {
    if (!inspection || seededForRef.current === inspection.id) return;

    seededForRef.current = inspection.id;

    setPhotos(
      (inspection.responses ?? []).flatMap((response) =>
        (response.documents ?? []).map((document) => ({
          key: nextKey(),
          subCategoryItemId: response.subCategoryItemId,
          status: 'done' as const,
          percent: 100,
          name: document.name,
          src: document.signedUrl ?? document.url,
          documentId: document.id,
        }))
      )
    );
  }, [inspection]);

  const patch = useCallback((key: string, changes: Partial<InspectionPhoto>) => {
    if (!mountedRef.current) return;
    setPhotos((current) =>
      current.map((photo) => (photo.key === key ? { ...photo, ...changes } : photo))
    );
  }, []);

  const upload = useCallback(
    async (photo: InspectionPhoto) => {
      const file = filesRef.current.get(photo.key);
      if (!inspectionId || !file) return;

      const controller = new AbortController();
      controllersRef.current.set(photo.key, controller);

      patch(photo.key, { status: 'uploading', percent: 0 });

      try {
        const compressed = await Utils.compressImage(file);

        const document = await inspectionStore.uploadResponseDocument(
          {
            inspectionId,
            subCategoryItemId: photo.subCategoryItemId,
            name: compressed.name,
            contentType: compressed.contentType,
            content: compressed.content,
          },
          {
            signal: controller.signal,
            onProgress: (percent) => patch(photo.key, { percent }),
          }
        );

        filesRef.current.delete(photo.key);

        patch(photo.key, {
          status: 'done',
          percent: 100,
          documentId: document.id,
          src: document.signedUrl ?? document.url,
        });
      } catch (error: any) {
        // An abort is us removing the thumbnail; the row is already gone.
        if (controller.signal.aborted) return;

        patch(photo.key, { status: 'error' });
      } finally {
        controllersRef.current.delete(photo.key);
      }
    },
    [inspectionId, inspectionStore, patch]
  );

  // Everything below reads the list through this rather than through state, so starting an upload,
  // warning about a limit and revoking a url all stay out of a setState updater - React is free to
  // run those twice.
  const add = useCallback(
    (subCategoryItemId: number, files: File[]) => {
      if (!inspectionId) return;

      const alreadyOnItem = photosRef.current.filter(
        (photo) => photo.subCategoryItemId === subCategoryItemId
      ).length;
      const room = MAX_PHOTOS_PER_ITEM - alreadyOnItem;

      if (room <= 0) {
        message.warning(`Up to ${MAX_PHOTOS_PER_ITEM} photos per item.`);
        return;
      }

      // Say so rather than dropping the extras quietly, which is what used to happen.
      if (files.length > room)
        message.warning(
          `Only ${room} more photo${room === 1 ? '' : 's'} fit on this item - the rest were skipped.`
        );

      const accepted = files.slice(0, room).map((file) => {
        const key = nextKey();
        const objectUrl = URL.createObjectURL(file);

        filesRef.current.set(key, file);
        objectUrlsRef.current.add(objectUrl);

        return {
          key,
          subCategoryItemId,
          status: 'uploading' as const,
          percent: 0,
          name: file.name,
          src: objectUrl,
        };
      });

      // The mirror is advanced here rather than waiting for the render, so picking twice before React
      // catches up still counts against the limit.
      photosRef.current = [...photosRef.current, ...accepted];
      setPhotos((current) => [...current, ...accepted]);

      // After the thumbnails are on screen, so the picture appears instantly and fills in behind.
      accepted.forEach((photo) => setTimeout(() => void upload(photo)));
    },
    [inspectionId, upload]
  );

  const remove = useCallback(
    async (key: string) => {
      const photo = photosRef.current.find((candidate) => candidate.key === key);
      if (!photo) return;

      controllersRef.current.get(key)?.abort();
      controllersRef.current.delete(key);
      filesRef.current.delete(key);

      setPhotos((current) => current.filter((candidate) => candidate.key !== key));

      if (!photo.documentId) return;

      try {
        await inspectionStore.deleteResponseDocument(photo.documentId);
      } catch {
        // It is still on the server; put it back rather than pretend it is gone.
        mountedRef.current && setPhotos((current) => [...current, photo]);
      }
    },
    [inspectionStore]
  );

  const retry = useCallback(
    (key: string) => {
      const photo = photosRef.current.find((candidate) => candidate.key === key);
      photo && void upload(photo);
    },
    [upload]
  );

  const retryAllFailed = useCallback(() => {
    photosRef.current
      .filter((photo) => photo.status === 'error')
      .forEach((photo) => void upload(photo));
  }, [upload]);

  const photosByItem = useMemo(() => {
    const grouped = new Map<number, InspectionPhoto[]>();

    photos.forEach((photo) => {
      const existing = grouped.get(photo.subCategoryItemId);
      existing ? existing.push(photo) : grouped.set(photo.subCategoryItemId, [photo]);
    });

    return grouped;
  }, [photos]);

  return {
    photosByItem,
    uploadingCount: photos.filter((photo) => photo.status === 'uploading').length,
    failedCount: photos.filter((photo) => photo.status === 'error').length,
    add,
    remove: (key: string) => void remove(key),
    retry,
    retryAllFailed,
  };
};
